import { useEffect, useState } from "react";
import {
  Users, GraduationCap, ShieldCheck, TrendingUp, Loader2, RefreshCw, FileText,
  AlertTriangle, LogOut, Clock, Activity, BarChart3, LineChart as LineIcon,
  UserMinus, Award, Palmtree, Send, Calendar, FileSearch, PieChart as PieIcon,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";

const GOLD = "#D4AF37";
const GOLD_SOFT = "#F5E6B8";
const EMERALD = "#34D399";
const AMBER = "#FBBF24";
const ROSE = "#F87171";
const SKY = "#38BDF8";
const VIOLET = "#A78BFA";
const ORANGE = "#FB923C";

function Stat({ icon: Icon, label, value, sub, tone = "gold", testId }) {
  const toneCls = {
    gold: "text-sheriff-gold border-sheriff-gold/30 bg-sheriff-gold/5",
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/5",
    sky: "text-sky-300 border-sky-500/30 bg-sky-500/5",
    violet: "text-violet-300 border-violet-500/30 bg-violet-500/5",
    orange: "text-orange-300 border-orange-500/30 bg-orange-500/5",
  }[tone] || "text-sheriff-gold border-sheriff-gold/30 bg-sheriff-gold/5";
  return (
    <div className={`rounded-2xl border p-4 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4 opacity-80" />
        <div className="text-[9px] uppercase tracking-widest opacity-60 font-mono">{sub || ""}</div>
      </div>
      <div className="font-heading font-black text-2xl leading-none" data-testid={`${testId}-value`}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-70 mt-2 font-mono">{label}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="h-px flex-1 bg-sheriff-gold/20" />
      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-sheriff-gold/80">{children}</div>
      <div className="h-px flex-1 bg-sheriff-gold/20" />
    </div>
  );
}

function ChartCard({ title, icon: Icon, children, testId, subtitle }) {
  return (
    <div className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-sheriff-gold" />
        <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-sheriff-gold">{title}</h3>
      </div>
      {subtitle && <div className="text-[10px] text-sheriff-gold/50 font-mono mb-3">{subtitle}</div>}
      <div className="h-64">{children}</div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-sheriff-gold/40 bg-[#0A1F18] px-3 py-2 shadow-xl">
      <div className="font-mono text-[10px] text-sheriff-gold/70 uppercase tracking-widest mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="text-xs flex items-center gap-2" style={{ color: p.color }}>
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="font-mono">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const PIE_COLORS = { pending: AMBER, accepted: EMERALD, rejected: ROSE };
const PIE_LABELS = { pending: "In sospeso", accepted: "Accettate", rejected: "Rifiutate" };

export default function ExecutiveStatsPanel() {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [roleDist, setRoleDist] = useState([]);
  const [candBreakdown, setCandBreakdown] = useState([]);
  const [topAuthors, setTopAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, tsRes, roleRes, candRes, mandAuthRes] = await Promise.all([
        api.get("/executive/stats"),
        api.get("/executive/stats/timeseries", { params: { days: 30 } }).catch(() => ({ data: { series: [] } })),
        api.get("/executive/stats/roles").catch(() => ({ data: { distribution: [] } })),
        api.get("/executive/stats/candidatures-breakdown").catch(() => ({ data: { breakdown: [] } })),
        api.get("/executive/stats/top-mandate-authors").catch(() => ({ data: { top: [] } })),
      ]);
      setData(statsRes.data);
      const s = (tsRes.data?.series || []).map((d) => ({
        ...d,
        label: d.date ? d.date.slice(5).replace("-", "/") : "",
      }));
      setSeries(s);
      setRoleDist(roleRes.data?.distribution || []);
      setCandBreakdown(candRes.data?.breakdown || []);
      setTopAuthors(mandAuthRes.data?.top || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare le statistiche");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const title = data?.sheriff_lastname
    ? `GESTIONE ${data.sheriff_lastname.toUpperCase()}`
    : "GESTIONE SCERIFFO";

  return (
    <div className="space-y-6" data-testid="executive-stats-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Panoramica Comando
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8]" data-testid="executive-stats-title">
            {title}
          </h2>
          {data?.sheriff_name && (
            <div className="text-sm text-[#D4C480] mt-1">
              Sceriffo in carica: <span className="text-sheriff-gold font-semibold">{data.sheriff_name}</span>
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="executive-stats-refresh"
          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full">
          {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sheriff-gold/70">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento statistiche…
        </div>
      ) : data ? (
        <>
          {/* PERSONALE */}
          <SectionTitle>Personale</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat icon={Users} label="Operatori attivi" value={data.total_active_operators} tone="gold" testId="stat-total-active" />
            <Stat icon={GraduationCap} label="Allievi" value={data.allievi_count} sub="in formazione" tone="sky" testId="stat-allievi" />
            <Stat icon={ShieldCheck} label="Comando" value={data.command_count} sub="Cap. +" tone="gold" testId="stat-command" />
            <Stat icon={ShieldCheck} label="Esecutivo" value={data.executive_count} sub="A. Sceriffo +" tone="gold" testId="stat-executive" />
            <Stat icon={Clock} label="Da approvare" value={data.pending_approval} sub="pending" tone={data.pending_approval > 0 ? "amber" : "gold"} testId="stat-pending-approval" />
          </div>

          {/* RECLUTAMENTO + PROMOZIONI + LICENZIAMENTI */}
          <SectionTitle>Movimenti personale</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={TrendingUp} label="Assunti 7gg" value={data.hired_last_week} tone="emerald" testId="stat-hired-week" />
            <Stat icon={TrendingUp} label="Assunti 30gg" value={data.hired_last_month} tone="emerald" testId="stat-hired-month" />
            <Stat icon={Award} label="Promossi 7gg" value={data.promotions_last_week} sub={`totali: ${data.promotions_total}`} tone="gold" testId="stat-promo-week" />
            <Stat icon={Award} label="Promossi 30gg" value={data.promotions_last_month} tone="gold" testId="stat-promo-month" />
            <Stat icon={UserMinus} label="Licenziati 7gg" value={data.fires_last_week} tone={data.fires_last_week > 0 ? "rose" : "gold"} testId="stat-fires-week" />
            <Stat icon={UserMinus} label="Licenziati 30gg" value={data.fires_last_month} sub={`totali: ${data.fires_total}`} tone={data.fires_last_month > 0 ? "rose" : "gold"} testId="stat-fires-month" />
            <Stat icon={LogOut} label="Dimissioni 30gg" value={data.resignations_last_month} sub={`pending: ${data.resignations_pending}`} tone={data.resignations_pending > 0 ? "rose" : "gold"} testId="stat-resign-month" />
            <Stat icon={LogOut} label="Cand. rifiutate 30gg" value={data.rejected_last_month} tone="orange" testId="stat-rejected" />
          </div>

          {/* OPERATIVITÀ */}
          <SectionTitle>Operatività</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Clock} label="Turni attivi" value={data.active_shifts} sub="in servizio ora" tone="emerald" testId="stat-active-shifts" />
            <Stat icon={Clock} label="Turni 24h" value={data.shifts_today} tone="emerald" testId="stat-shifts-today" />
            <Stat icon={Clock} label="Turni 7gg" value={data.shifts_last_week} tone="emerald" testId="stat-shifts-week" />
            <Stat icon={Activity} label="Ore servizio 7gg" value={data.total_service_hours_week} sub="totali" tone="emerald" testId="stat-service-hours" />
            <Stat icon={FileText} label="Mandati 7gg" value={data.mandates_last_week} sub={`totali: ${data.mandates_total}`} tone="gold" testId="stat-mandates-week" />
            <Stat icon={FileText} label="Mandati 30gg" value={data.mandates_last_month} tone="gold" testId="stat-mandates-month" />
            <Stat icon={FileSearch} label="Report Detective 30gg" value={data.detective_reports_last_month} sub={`tot: ${data.detective_reports_total}`} tone="violet" testId="stat-detective" />
            <Stat icon={Palmtree} label="Ferie approvate 30gg" value={data.vacations_approved_last_month} sub={`pending: ${data.vacations_pending}`} tone="sky" testId="stat-vacations" />
          </div>

          {/* DISCIPLINA */}
          <SectionTitle>Disciplina & Compliance</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={AlertTriangle} label="Sanzioni 7gg" value={data.warns_last_week} tone={data.warns_last_week > 0 ? "amber" : "gold"} testId="stat-warns-week" />
            <Stat icon={AlertTriangle} label="Sanzioni 30gg" value={data.warns_last_month} sub={`totali: ${data.warns_total}`} tone={data.warns_last_month > 0 ? "amber" : "gold"} testId="stat-warns-month" />
            <Stat icon={AlertTriangle} label="Sanzioni attive" value={data.active_sanctions} tone={data.active_sanctions > 0 ? "amber" : "gold"} testId="stat-active-sanctions" />
            <Stat icon={GraduationCap} label="Esami richiesti" value={data.exam_auth_pending} sub="pending" tone={data.exam_auth_pending > 0 ? "amber" : "gold"} testId="stat-exam-auth" />
          </div>

          {/* COMMUNITY */}
          <SectionTitle>Community & Reclutamento</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Users} label="Candidature pending" value={data.candidatures_pending} sub={`totali: ${data.candidatures_total}`} tone={data.candidatures_pending > 0 ? "amber" : "gold"} testId="stat-cand-pending" />
            <Stat icon={Send} label="Inviti Discord 30gg" value={data.invites_sent_last_month} sub={`totali: ${data.invites_total}`} tone="violet" testId="stat-invites" />
            <Stat icon={Calendar} label="Eventi in agenda" value={data.events_upcoming} sub="futuri" tone="sky" testId="stat-events" />
            <Stat icon={GraduationCap} label="Tentativi esame 30gg" value={data.exam_attempts_last_month} tone="sky" testId="stat-exam-attempts" />
          </div>

          {/* CHARTS ROW 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-4">
            <ChartCard title="Attività ultimi 30 giorni" icon={LineIcon} testId="chart-timeseries"
              subtitle="Assunzioni · Promozioni · Mandati · Sanzioni · Licenziamenti · Rifiuti">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    {[
                      ["gHires", EMERALD], ["gMand", GOLD], ["gWarn", ROSE],
                      ["gFires", "#DC2626"], ["gPromo", SKY], ["gRej", ORANGE],
                    ].map(([id, color]) => (
                      <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A4A3E" />
                  <XAxis dataKey="label" stroke="#8B7B49" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis stroke="#8B7B49" tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", color: GOLD_SOFT }} />
                  <Area type="monotone" dataKey="hires" name="Assunzioni" stroke={EMERALD} strokeWidth={2} fill="url(#gHires)" />
                  <Area type="monotone" dataKey="promotions" name="Promozioni" stroke={SKY} strokeWidth={2} fill="url(#gPromo)" />
                  <Area type="monotone" dataKey="mandates" name="Mandati" stroke={GOLD} strokeWidth={2} fill="url(#gMand)" />
                  <Area type="monotone" dataKey="warns" name="Sanzioni" stroke={ROSE} strokeWidth={2} fill="url(#gWarn)" />
                  <Area type="monotone" dataKey="fires" name="Licenziamenti" stroke="#DC2626" strokeWidth={2} fill="url(#gFires)" />
                  <Area type="monotone" dataKey="rejections" name="Rifiuti cand." stroke={ORANGE} strokeWidth={2} fill="url(#gRej)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Distribuzione per grado" icon={BarChart3} testId="chart-roles" subtitle="Operatori attivi per rank">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roleDist} margin={{ top: 5, right: 10, left: -20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A4A3E" />
                  <XAxis dataKey="role" stroke="#8B7B49" tick={{ fontSize: 9, fontFamily: "monospace" }}
                    angle={-40} textAnchor="end" interval={0} height={60} />
                  <YAxis stroke="#8B7B49" tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Operatori" fill={GOLD} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* CHARTS ROW 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Candidature per stato" icon={PieIcon} testId="chart-candidatures" subtitle="Distribuzione totale">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={candBreakdown.map((d) => ({ ...d, name: PIE_LABELS[d.status] || d.status }))}
                    dataKey="count"
                    nameKey="name"
                    cx="50%" cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count}`}
                  >
                    {candBreakdown.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.status] || GOLD} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", color: GOLD_SOFT }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Trend assunzioni vs licenziamenti" icon={TrendingUp} testId="chart-hires-fires" subtitle="Bilancio netto 30gg">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A4A3E" />
                  <XAxis dataKey="label" stroke="#8B7B49" tick={{ fontSize: 9, fontFamily: "monospace" }} />
                  <YAxis stroke="#8B7B49" tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", color: GOLD_SOFT }} />
                  <Bar dataKey="hires" name="Assunzioni" stackId="a" fill={EMERALD} />
                  <Bar dataKey="fires" name="Licenziamenti" stackId="b" fill="#DC2626" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Top autori mandati (30gg)" icon={FileText} testId="chart-top-authors" subtitle="Chi ha emesso più mandati">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topAuthors} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A4A3E" />
                  <XAxis type="number" stroke="#8B7B49" tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="author" stroke="#8B7B49"
                    tick={{ fontSize: 9, fontFamily: "monospace" }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Mandati" fill={VIOLET} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="text-[10px] uppercase tracking-widest text-sheriff-gold/40 font-mono text-right">
            Dati aggiornati al {new Date(data.generated_at).toLocaleString("it-IT")}
          </div>
        </>
      ) : (
        <div className="text-sheriff-gold/60 text-sm">Nessun dato disponibile.</div>
      )}
    </div>
  );
}
